import React, { useState, useEffect } from 'react';
import './SessionTracker.css';
import { WorkoutSession, ExerciseSession } from '../models/SessionTracker';
import { StorageService } from '../services/StorageService';
import { CATEGORY_TO_PRIMARY, EXERCISE_LIBRARY, getExerciseByName } from '../data/exercises';

function SessionTracker({ routine, onClose, onLogWorkout }) {
  const [session, setSession] = useState(null);
  const [expandedExercise, setExpandedExercise] = useState(null);
  const [newExercise, setNewExercise] = useState('');
  const [exerciseSearch, setExerciseSearch] = useState('');
  const [showExerciseDropdown, setShowExerciseDropdown] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [equipmentFilter, setEquipmentFilter] = useState('all');
  const [primaryMuscleFilter, setPrimaryMuscleFilter] = useState('all');
  const [secondaryMuscleFilter, setSecondaryMuscleFilter] = useState('all');

  const formatLabel = (value) => value
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  const buildExerciseSession = (exercise) => {
    const historyEntry = StorageService.getExerciseHistoryEntry(exercise.name);
    const targetSets = historyEntry?.sets ?? exercise.sets ?? 3;
    const targetReps = historyEntry?.reps ?? exercise.reps ?? 10;
    const defaultWeight = historyEntry?.weight ?? null;

    return new ExerciseSession({
      exerciseName: exercise.name,
      targetSets,
      targetReps,
      defaultWeight
    });
  };


  // Initialize session from routine
  useEffect(() => {
    if (routine) {
      const exercises = routine.exercises?.map(ex => {
        return buildExerciseSession(ex);
      }) || [];

      const newSession = new WorkoutSession({
        name: routine.name || 'Workout Session',
        exercises: exercises
      });
      
      setSession(newSession);
    }
  }, [routine]);

  useEffect(() => {
    if (categoryFilter === 'all') {
      setPrimaryMuscleFilter('all');
      return;
    }
    const mappedPrimary = CATEGORY_TO_PRIMARY[categoryFilter] || categoryFilter;
    const hasPrimary = EXERCISE_LIBRARY.some(exercise =>
      (exercise.primaryMuscles || []).includes(mappedPrimary)
    );
    const nextPrimary = hasPrimary ? mappedPrimary : 'all';
    setPrimaryMuscleFilter(nextPrimary);
  }, [categoryFilter]);

  const handleCompleteSet = (exerciseId, setId) => {
    setSession(prev => {
      const updated = { ...prev };
      const exercise = updated.exercises.find(e => e.id === exerciseId);
      if (exercise) {
        exercise.completeSet(setId);
      }
      return updated;
    });
  };

  const handleDeleteSet = (exerciseId, setId) => {
    setSession(prev => {
      const updated = { ...prev };
      const exercise = updated.exercises.find(e => e.id === exerciseId);
      if (exercise) {
        exercise.deleteSet(setId);
      }
      return updated;
    });
  };

  const handleAddSet = (exerciseId) => {
    setSession(prev => {
      const updated = { ...prev };
      const exercise = updated.exercises.find(e => e.id === exerciseId);
      if (exercise) {
        exercise.addSet();
      }
      return updated;
    });
  };

  const handleUpdateSet = (exerciseId, setId, field, value) => {
    setSession(prev => {
      const updated = { ...prev };
      const exercise = updated.exercises.find(e => e.id === exerciseId);
      if (exercise) {
        const set = exercise.sets.find(s => s.id === setId);
        if (set) {
          if (field === 'completed') {
            set[field] = !set[field];
          } else if (field === 'reps') {
            set[field] = value === '' ? '' : parseInt(value, 10) || 0;
          } else if (field === 'weight') {
            set[field] = value === '' ? null : parseFloat(value);
          } else {
            set[field] = value;
          }
        }
      }
      return updated;
    });
  };

  const adjustReps = (exerciseId, setId, delta) => {
    setSession(prev => {
      const updated = { ...prev };
      const exercise = updated.exercises.find(e => e.id === exerciseId);
      if (exercise) {
        const set = exercise.sets.find(s => s.id === setId);
        if (set) {
          const current = typeof set.reps === 'number' ? set.reps : parseInt(set.reps, 10) || 0;
          const next = Math.max(0, current + delta);
          set.reps = next;
        }
      }
      return updated;
    });
  };

  const handleAddExercise = () => {
    if (!newExercise.trim()) return;

    setSession(prev => {
      const updated = { ...prev };
      const trimmedName = newExercise.trim();
      const historyEntry = StorageService.getExerciseHistoryEntry(trimmedName);
      const libraryDefaults = getExerciseByName(trimmedName);
      const newEx = new ExerciseSession({
        exerciseName: trimmedName,
        targetSets: historyEntry?.sets ?? libraryDefaults?.sets ?? 3,
        targetReps: historyEntry?.reps ?? libraryDefaults?.reps ?? 10,
        defaultWeight: historyEntry?.weight ?? null
      });
      updated.exercises.push(newEx);
      return updated;
    });
    setNewExercise('');
    setExerciseSearch('');
    setShowExerciseDropdown(false);
  };

  const categories = ['all', ...new Set(EXERCISE_LIBRARY.map(exercise => exercise.category))].sort();
  const equipmentOptions = ['all', ...new Set(EXERCISE_LIBRARY.flatMap(exercise => exercise.equipment || []))].sort();
  const primaryMuscleOptions = ['all', ...new Set(EXERCISE_LIBRARY.flatMap(exercise => exercise.primaryMuscles || []))].sort();
  const secondaryMuscleOptions = ['all', ...new Set(EXERCISE_LIBRARY.flatMap(exercise => exercise.secondaryMuscles || []))].sort();

  const filteredExercises = EXERCISE_LIBRARY.filter(exercise => {
    const matchesSearch = exercise.name.toLowerCase().includes(exerciseSearch.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || exercise.category === categoryFilter;
    const matchesEquipment =
      equipmentFilter === 'all' || (exercise.equipment || []).includes(equipmentFilter);
    const matchesPrimary =
      primaryMuscleFilter === 'all' || (exercise.primaryMuscles || []).includes(primaryMuscleFilter);
    const matchesSecondary =
      secondaryMuscleFilter === 'all' || (exercise.secondaryMuscles || []).includes(secondaryMuscleFilter);
    return matchesSearch && matchesCategory && matchesEquipment && matchesPrimary && matchesSecondary;
  });

  const handleCopyExercise = (exerciseId) => {
    setSession(prev => {
      const updated = { ...prev };
      const exercise = updated.exercises.find(e => e.id === exerciseId);
      if (exercise) {
        const copied = new ExerciseSession({
          exerciseName: exercise.exerciseName,
          targetSets: exercise.targetSets,
          targetReps: exercise.targetReps,
          defaultWeight: exercise.defaultWeight
        });
        updated.exercises.push(copied);
      }
      return updated;
    });
  };

  const handleDeleteExercise = (exerciseId) => {
    setSession(prev => {
      const updated = { ...prev };
      updated.exercises = updated.exercises.filter(e => e.id !== exerciseId);
      return updated;
    });
  };

  const handleCopySession = () => {
    setSession(prev => {
      const copied = new WorkoutSession({
        name: `${prev.name} (Copy)`,
        exercises: prev.exercises.map(ex => 
          new ExerciseSession({
            exerciseName: ex.exerciseName,
            targetSets: ex.targetSets,
            targetReps: ex.targetReps,
            defaultWeight: ex.defaultWeight
          })
        )
      });
      return copied;
    });
  };

  const handleLogWorkout = () => {
    if (session && onLogWorkout) {
      // Calculate XP reward based on exercises completed
      const xpReward = session.exercises.length * 50; // 50 XP per exercise
      onLogWorkout(session, xpReward);
      onClose();
    }
  };

  if (!session) {
    return (
      <div className="session-tracker-overlay" onClick={onClose}>
        <div className="session-tracker" onClick={(e) => e.stopPropagation()}>
          <p>Loading session...</p>
        </div>
      </div>
    );
  }

  const progress = session && typeof session.getProgress === 'function' 
    ? session.getProgress() 
    : { completed: 0, total: 0, percentage: 0 };

  return (
    <div className="session-tracker-overlay" onClick={onClose}>
      <div className="session-tracker" onClick={(e) => e.stopPropagation()}>
        <div className="session-header">
          <h1> Session Tracker</h1>
          <h2>{session.name}</h2>
          <button className="close-btn" onClick={onClose}></button>
        </div>

        {/* Progress Bar */}
        <div className="session-progress">
          <div className="progress-info">
            <span>{progress.completed}/{progress.total} Exercises Complete</span>
            <span>{Math.round(progress.percentage)}%</span>
          </div>
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${progress.percentage}%` }}
            ></div>
          </div>
        </div>

        {/* Exercises List */}
        <div className="exercises-list">
          {session.exercises.map((exercise) => {
            const exProgress = exercise.getProgress();
            const isExpanded = expandedExercise === exercise.id;
            const exerciseMeta = getExerciseByName(exercise.exerciseName);
            const categoryLabel = exerciseMeta?.category ? formatLabel(exerciseMeta.category) : 'Custom';
            const primaryMuscles = exerciseMeta?.primaryMuscles || [];
            const secondaryMuscles = exerciseMeta?.secondaryMuscles || [];
            const equipmentList = exerciseMeta?.equipment || [];

            return (
              <div key={exercise.id} className="exercise-card">
                <div 
                  className="exercise-header"
                  onClick={() => setExpandedExercise(isExpanded ? null : exercise.id)}
                >
                  <div className="exercise-title-section">
                    <h3>{exercise.exerciseName}</h3>
                    <div className="exercise-chip-row">
                      <span className="exercise-chip chip-category">{categoryLabel}</span>
                      {primaryMuscles.map((muscle) => (
                        <span key={`primary-${exercise.id}-${muscle}`} className="exercise-chip chip-primary">
                          {formatLabel(muscle)}
                        </span>
                      ))}
                      {secondaryMuscles.map((muscle) => (
                        <span key={`secondary-${exercise.id}-${muscle}`} className="exercise-chip chip-secondary">
                          {formatLabel(muscle)}
                        </span>
                      ))}
                      {equipmentList.map((equipment) => (
                        <span key={`equipment-${exercise.id}-${equipment}`} className="exercise-chip chip-equipment">
                          {formatLabel(equipment)}
                        </span>
                      ))}
                    </div>
                    <span className="exercise-progress">
                      {exProgress.completed}/{exProgress.total} Sets
                    </span>
                  </div>
                  <span className={`expand-arrow ${isExpanded ? 'expanded' : ''}`}>
                    
                  </span>
                </div>

                {isExpanded && (
                  <div className="exercise-details">
                    <table className="sets-table">
                      <thead>
                        <tr>
                          <th>Set</th>
                          <th>Reps</th>
                          <th>Weight</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {exercise.sets.map((set) => (
                          <tr key={set.id} className={set.completed ? 'completed' : ''}>
                            <td>
                              <input
                                type="number"
                                value={set.setNumber}
                                readOnly
                                className="set-number"
                              />
                            </td>
                            <td>
                              <div className="reps-control">
                                <button
                                  type="button"
                                  className="reps-btn"
                                  onClick={() => adjustReps(exercise.id, set.id, -1)}
                                  aria-label="Decrease reps"
                                >
                                  -
                                </button>
                                <input
                                  type="number"
                                  value={set.reps === '' ? '' : (set.reps ?? 0)}
                                  onChange={(e) => handleUpdateSet(exercise.id, set.id, 'reps', e.target.value)}
                                  className="set-input"
                                  min="0"
                                />
                                <button
                                  type="button"
                                  className="reps-btn"
                                  onClick={() => adjustReps(exercise.id, set.id, 1)}
                                  aria-label="Increase reps"
                                >
                                  +
                                </button>
                              </div>
                            </td>
                            <td>
                              <input
                                type="number"
                                value={set.weight ?? ''}
                                onChange={(e) => handleUpdateSet(exercise.id, set.id, 'weight', e.target.value)}
                                className="set-input"
                                min="0"
                                step="0.5"
                                placeholder="Weight"
                              />
                            </td>
                            <td className="actions-cell">
                              <button
                                className={`btn-complete ${set.completed ? 'completed' : ''}`}
                                onClick={() => handleCompleteSet(exercise.id, set.id)}
                                title={set.completed ? 'Mark Incomplete' : 'Mark Complete'}
                              >
                                
                              </button>
                              <button
                                className="btn-delete"
                                onClick={() => handleDeleteSet(exercise.id, set.id)}
                                title="Delete Set"
                              >
                                
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    <div className="set-actions">
                      <button 
                        className="btn-add-set"
                        onClick={() => handleAddSet(exercise.id)}
                      >
                        + Add Set
                      </button>
                    </div>
                  </div>
                )}

                <div className="exercise-footer">
                  <button
                    className="btn-copy"
                    onClick={() => handleCopyExercise(exercise.id)}
                    title="Copy Exercise"
                  >
                     Copy
                  </button>
                  <button
                    className="btn-remove"
                    onClick={() => handleDeleteExercise(exercise.id)}
                    title="Remove Exercise"
                  >
                     Remove
                  </button>
                </div>
              </div>
            );
          })}

          {/* Add New Exercise - Inside exercises list */}
          <div className="add-exercise-card">
            <h3>Add Exercise to Session</h3>
            <div className="add-exercise-form">
              <div className="exercise-search-wrapper">
                <div className="exercise-filter-bar">
                  <select 
                    value={categoryFilter} 
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="exercise-filter-select"
                  >
                    {categories.map(category => (
                      <option key={category} value={category}>
                        {category === 'all' ? ' All Categories' : formatLabel(category)}
                      </option>
                    ))}
                  </select>
                  <select
                    value={equipmentFilter}
                    onChange={(e) => setEquipmentFilter(e.target.value)}
                    className="exercise-filter-select"
                  >
                    {equipmentOptions.map(option => (
                      <option key={option} value={option}>
                        {option === 'all' ? ' All Equipment' : formatLabel(option)}
                      </option>
                    ))}
                  </select>
                  <select
                    value={primaryMuscleFilter}
                    onChange={(e) => setPrimaryMuscleFilter(e.target.value)}
                    className="exercise-filter-select"
                  >
                    {primaryMuscleOptions.map(option => (
                      <option key={option} value={option}>
                        {option === 'all' ? ' Primary Muscle' : formatLabel(option)}
                      </option>
                    ))}
                  </select>
                  <select
                    value={secondaryMuscleFilter}
                    onChange={(e) => setSecondaryMuscleFilter(e.target.value)}
                    className="exercise-filter-select"
                  >
                    {secondaryMuscleOptions.map(option => (
                      <option key={option} value={option}>
                        {option === 'all' ? ' Secondary Muscle' : formatLabel(option)}
                      </option>
                    ))}
                  </select>
                </div>
                <input
                  type="text"
                  placeholder=" Search exercises..."
                  value={exerciseSearch}
                  onChange={(e) => {
                    setExerciseSearch(e.target.value);
                    setShowExerciseDropdown(true);
                  }}
                  onFocus={() => setShowExerciseDropdown(true)}
                  className="exercise-search-input"
                />
                {showExerciseDropdown && (
                  <div className="exercise-dropdown-menu">
                    {filteredExercises.map((exerciseOption) => (
                      <div
                        key={exerciseOption.name}
                        className="exercise-dropdown-item"
                        onClick={() => {
                          setNewExercise(exerciseOption.name);
                          setExerciseSearch('');
                          setShowExerciseDropdown(false);
                        }}
                      >
                        <div className="exercise-option-name">{exerciseOption.name}</div>
                        <div className="exercise-option-chips">
                          <span className="exercise-chip chip-category">{formatLabel(exerciseOption.category)}</span>
                          {(exerciseOption.primaryMuscles || []).map((muscle) => (
                            <span key={`${exerciseOption.name}-primary-${muscle}`} className="exercise-chip chip-primary">
                              {formatLabel(muscle)}
                            </span>
                          ))}
                          {(exerciseOption.secondaryMuscles || []).map((muscle) => (
                            <span key={`${exerciseOption.name}-secondary-${muscle}`} className="exercise-chip chip-secondary">
                              {formatLabel(muscle)}
                            </span>
                          ))}
                          {(exerciseOption.equipment || []).map((equipment) => (
                            <span key={`${exerciseOption.name}-equipment-${equipment}`} className="exercise-chip chip-equipment">
                              {formatLabel(equipment)}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                    {filteredExercises.length === 0 && (
                      <div className="exercise-dropdown-empty">
                        No exercises match your filters.
                      </div>
                    )}
                  </div>
                )}
              </div>
              {newExercise && (
                <div className="selected-exercise-display">
                   {newExercise}
                </div>
              )}
              <button 
                className="btn-add-exercise"
                onClick={handleAddExercise}
                disabled={!newExercise.trim()}
              >
                + Add Exercise
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Actions */}
        <div className="session-actions">
          <button 
            className="btn-copy-session"
            onClick={handleCopySession}
          >
             Copy Session
          </button>
          <button 
            className="btn-log-workout"
            onClick={handleLogWorkout}
            disabled={session.exercises.length === 0}
          >
             Log Workout
          </button>
        </div>
      </div>
    </div>
  );
}

export default SessionTracker;
